import type { CSSProperties } from 'react';
import { wrapFontNamesWithQuotes } from 'common/utils/css-font-family';
import {
	getTextPresetInheritedAssignment,
	getTextPresetTokenId,
	normalizePlatformStyleValue,
	resolveDesignModeValue,
	resolveDesignTokenReference,
	type DesignDefinition,
	type DesignModeSelection,
	type TextPreset,
	type TextPresetProperty,
	type TextPresetPropertyValue,
} from 'common/design-tokens';
import { getPlatformReferenceWidth } from 'common/utils/story-media-query';
import { convertVwToPx } from 'utils/helpers';
import type { StoryDefaultPlatform, StoryMediaQuery } from 'types/story';

/** Resolve inheritance before converting: inherited vw keeps its authored platform's reference width. */
export function getTextPresetDisplayValue({
	property,
	assignment,
	platform,
	mediaQuery,
	design,
	modeSelection,
}: {
	property: TextPresetProperty;
	assignment: TextPresetPropertyValue;
	platform: 'base' | StoryDefaultPlatform;
	mediaQuery: StoryMediaQuery;
	design: DesignDefinition;
	modeSelection: DesignModeSelection;
}) {
	const inherited =
		platform === 'base'
			? assignment.base === undefined
				? undefined
				: { value: assignment.base, source: 'Base' }
			: getTextPresetInheritedAssignment(assignment, platform, mediaQuery);
	if (!inherited) return undefined;
	const token = resolveDesignTokenReference(String(inherited.value), design.tokens);
	if (getTextPresetTokenId(inherited.value) && !token) return undefined;
	const authoredValue = token ? resolveDesignModeValue<string | number>(token.value, modeSelection) : inherited.value;
	const source = (
		inherited.source === 'Base' ? mediaQuery.defaultPlatform : inherited.source
	) as StoryDefaultPlatform;
	const cssValue =
		platform === 'base' ? authoredValue : normalizePlatformStyleValue(property, authoredValue, mediaQuery, source);
	const value =
		typeof cssValue === 'string' && cssValue.endsWith('vw')
			? `${convertVwToPx(cssValue, 2, getPlatformReferenceWidth(mediaQuery, platform === 'base' ? mediaQuery.defaultPlatform : platform))}px`
			: authoredValue;
	return { value, cssValue, authoredValue };
}

/** Preview uses every resolved assignment, with the same font-family serialization as runtime. */
export function getTextPresetPreviewStyle({
	preset,
	...context
}: Omit<Parameters<typeof getTextPresetDisplayValue>[0], 'property' | 'assignment'> & {
	preset: TextPreset;
}): CSSProperties {
	const style = Object.fromEntries(
		Object.entries(preset.properties).flatMap(([property, assignment]) => {
			const display = getTextPresetDisplayValue({
				...context,
				property: property as TextPresetProperty,
				assignment,
			});
			return display ? [[property, display.value]] : [];
		})
	);
	return {
		...style,
		fontFamily: wrapFontNamesWithQuotes(
			String(
				style.fontFamily ??
					resolveDesignModeValue(context.design.defaults.root.fontFamily, context.modeSelection)
			)
		),
	};
}
